import { lazy, Suspense, useEffect, type ReactNode } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { Carregando, Mensagem } from './components/ui'
import { AdminAuditoria, AdminPapeis, AdminUsuarios } from './features/admin/paginas'
import { CriarConta } from './features/auth/CriarConta'
import { Entrar } from './features/auth/Entrar'
import { AtivarMfa, MfaObrigatorio } from './features/auth/Mfa'
import { ConfirmarEmail, EsqueciSenha, RedefinirSenha } from './features/auth/paginas-senha'
import { useAuth } from './features/auth/store'
import { MinhaConta } from './features/dashboard/MinhaConta'
import { Painel } from './features/dashboard/Painel'
import { Shell } from './features/shell/Shell'

/**
 * Telas com MAPA carregadas sob demanda.
 *
 * O Leaflet pesa ~45 kB gzip e so e usado nestas rotas. Carregando tudo junto,
 * o bundle inicial chegava a 243 kB gzip — encostado no teto de 250 kB que o
 * SPEC 9.2 estabelece para 4G. Com lazy, quem abre o login baixa o minimo, e o
 * mapa vem quando (e se) a pessoa navegar para uma tela que o use.
 *
 * A tela de verificacao entra aqui pelo mesmo motivo: e usada por um punhado de
 * operadores, nao por todo motorista.
 */
// A pagina inicial publica e a unica rota que um visitante anonimo alcanca:
// carrega em pedaco separado para nao entrar no pacote de quem ja tem sessao.
const Inicio = lazy(() => import('./features/publico/Inicio').then((m) => ({ default: m.Inicio })))
const Planejador = lazy(() =>
  import('./features/viagem/Planejador').then((m) => ({ default: m.Planejador })),
)
const Vitrine = lazy(() =>
  import('./features/fretes/Vitrine').then((m) => ({ default: m.Vitrine })),
)
const DetalheFrete = lazy(() =>
  import('./features/fretes/DetalheFrete').then((m) => ({ default: m.DetalheFrete })),
)
const PublicarFrete = lazy(() =>
  import('./features/fretes/PublicarFrete').then((m) => ({ default: m.PublicarFrete })),
)
const CadastroMotorista = lazy(() =>
  import('./features/motorista/Cadastro').then((m) => ({ default: m.CadastroMotorista })),
)
const AgendaMotorista = lazy(() =>
  import('./features/motorista/Agenda').then((m) => ({ default: m.AgendaMotorista })),
)
const FilaVerificacao = lazy(() =>
  import('./features/operacao/Verificacoes').then((m) => ({ default: m.FilaVerificacao })),
)

/**
 * Passe GPlanSiS na entrada do sistema: sem sessao, enquanto a tentativa do
 * passe (uma por carga) nao terminou, mostra "verificando" em vez do formulario
 * de senha ou do redirecionamento — o login nao chega a piscar. Na pagina
 * publica (`/`, `/inicio`) isto nao roda: a vitrine fica igual a do anonimo.
 */
function PasseNaEntrada({ children }: { children: ReactNode }) {
  const { usuario, carregando, passe, tentarPasse } = useAuth()
  const aguardar = !carregando && !usuario && passe !== 'feito'
  useEffect(() => {
    if (aguardar) void tentarPasse()
  }, [aguardar, tentarPasse])
  if (aguardar) return <Carregando rotulo="Verificando sua sessão" />
  return <>{children}</>
}

/**
 * Guarda de rota.
 *
 * IMPORTANTE: esta guarda e conveniencia de navegacao, NAO controle de acesso.
 * Quem autoriza e o servidor (SPEC 5.4). Se alguem burlar isto no navegador,
 * encontra telas vazias e 403 da API — nunca dado alheio.
 */
function Protegida({
  children,
  permissao,
}: {
  children: React.ReactNode
  permissao?: string
}) {
  const { usuario, carregando, pode, passe } = useAuth()
  const local = useLocation()

  // Enquanto o bootstrap nao terminou, nao decidimos nada: mandar para o login
  // aqui faria a tela piscar a cada F5 de quem esta logado.
  if (carregando) return <Carregando rotulo="Verificando sua sessão" />

  if (!usuario) {
    // Rota do sistema sem sessao: antes de mandar ao login, o Passe GPlanSiS.
    if (passe !== 'feito') return <PasseNaEntrada>{null}</PasseNaEntrada>
    // `state` guarda o destino para voltar a ele depois do login — COM a query
    // string. Guardar so o pathname descartava os parametros: quem preenchia a
    // busca na vitrine e caia no login voltava para /planejar vazio, tendo de
    // digitar origem e destino de novo.
    return <Navigate to="/entrar" replace state={{ de: local.pathname + local.search }} />
  }

  if (permissao && !pode(permissao)) {
    return (
      <div className="ifr-pagina ifr-pagina--forma">
        {/* h1 de verdade: o `titulo` da Mensagem sai como <strong>, que nao e
            cabecalho. Sem ele esta tela nao tinha cabecalho NENHUM — e ela e
            justamente uma que precisa ser encontrada e entendida rapido, porque
            aparece quando a pessoa clicou em algo que nao pode acessar. */}
        <h1 className="ifr-titulo">Sem permissão</h1>
        <Mensagem tipo="danger">
          Sua conta não tem a permissão <code>{permissao}</code> necessária para esta tela. Se você
          precisa dela, fale com um administrador.
        </Mensagem>
      </div>
    )
  }

  return <>{children}</>
}

/**
 * A raiz: vitrine para visitante, painel para quem tem sessao.
 *
 * A decisao espera o fim do bootstrap. Sem isso, quem esta logado veria a
 * pagina de marketing por um instante a cada F5 na raiz.
 */
function Raiz() {
  const { usuario, carregando } = useAuth()
  if (carregando) return <Carregando rotulo="Carregando" />
  if (usuario) return <Navigate to="/painel" replace />
  // Suspense OBRIGATORIO: `Inicio` e carregada por `lazy`, e um componente que
  // suspende sem fronteira acima dele derruba a arvore inteira (React #426) —
  // o resultado era a pagina inicial em branco, com o erro so no console.
  return (
    <Suspense fallback={<Carregando rotulo="Carregando" />}>
      <Inicio />
    </Suspense>
  )
}

/** Rota so para quem NAO esta logado (login, cadastro). */
function Publica({ children }: { children: React.ReactNode }) {
  const { usuario, carregando } = useAuth()
  if (carregando) return <Carregando rotulo="Verificando sua sessão" />
  if (usuario) return <Navigate to="/painel" replace />
  return <>{children}</>
}

function NaoEncontrada() {
  return (
    <div className="ifr-pagina ifr-pagina--forma">
      <h1 className="ifr-titulo">Página não encontrada</h1>
      <p className="ifr-apoio">O endereço acessado não existe no iFretes.</p>
      <a href="/painel" className="br-button primary">
        Ir para o painel
      </a>
    </div>
  )
}

export function App() {
  const bootstrap = useAuth((s) => s.bootstrap)

  // Uma unica tentativa de reconstituir a sessao a partir do cookie httpOnly.
  // O access token vive em memoria, entao todo carregamento passa por aqui.
  useEffect(() => {
    void bootstrap()
  }, [bootstrap])

  return (
    <BrowserRouter>
      <Routes>
        {/* ---------------------------------------------------- públicas */}
        <Route
          path="/entrar"
          element={
            <Publica>
              {/* "Entrar" e a porta do sistema: aqui (e nas rotas protegidas) o
                  Passe GPlanSiS entra sem pedir senha. */}
              <PasseNaEntrada>
                <Entrar />
              </PasseNaEntrada>
            </Publica>
          }
        />
        <Route
          path="/criar-conta"
          element={
            <Publica>
              <CriarConta />
            </Publica>
          }
        />
        <Route path="/esqueci-senha" element={<EsqueciSenha />} />
        {/* Confirmação e redefinição NÃO usam <Publica>: chegam por link de
            e-mail e precisam funcionar mesmo com sessão aberta em outra aba. */}
        <Route path="/verificar-email" element={<ConfirmarEmail />} />
        <Route path="/redefinir-senha" element={<RedefinirSenha />} />
        <Route path="/mfa-obrigatorio" element={<MfaObrigatorio />} />

        {/* ---------------------------------------------------- protegidas */}
        <Route
          element={
            <Protegida>
              <Shell />
            </Protegida>
          }
        >
          <Route path="/painel" element={<Painel />} />
          <Route path="/minha-conta" element={<MinhaConta />} />
          <Route path="/minha-conta/mfa" element={<AtivarMfa />} />

          {/* Planejador multimodal — o mapa e a comparacao de modais. */}
          <Route
            path="/planejar"
            element={
              <Protegida permissao="freight:read:public">
                <Suspense fallback={<Carregando rotulo="Abrindo o planejador" />}>
                  <Planejador />
                </Suspense>
              </Protegida>
            }
          />

          {/* ── Onda 3: vitrine e publicacao ── */}
          <Route
            path="/fretes"
            element={
              <Protegida permissao="freight:read:public">
                <Suspense fallback={<Carregando rotulo="Abrindo a vitrine" />}>
                  <Vitrine />
                </Suspense>
              </Protegida>
            }
          />
          <Route
            path="/fretes/novo"
            element={
              <Protegida permissao="freight:create">
                <Suspense fallback={<Carregando rotulo="Abrindo o formulário" />}>
                  <PublicarFrete />
                </Suspense>
              </Protegida>
            }
          />
          {/* Esta rota vem DEPOIS de /fretes/novo: o React Router casa na ordem,
              e um :id antes capturaria "novo" como se fosse um identificador. */}
          <Route
            path="/fretes/:id"
            element={
              <Protegida permissao="freight:read:public">
                <Suspense fallback={<Carregando rotulo="Abrindo o frete" />}>
                  <DetalheFrete />
                </Suspense>
              </Protegida>
            }
          />

          {/* ── Onda 2: cadastro e verificacao ── */}
          <Route
            path="/motorista/cadastro"
            element={
              <Protegida permissao="driver:update:own">
                <Suspense fallback={<Carregando rotulo="Abrindo seu cadastro" />}>
                  <CadastroMotorista />
                </Suspense>
              </Protegida>
            }
          />
          <Route
            path="/motorista/agenda"
            element={
              <Protegida permissao="driver:update:own">
                <Suspense fallback={<Carregando rotulo="Abrindo sua agenda" />}>
                  <AgendaMotorista />
                </Suspense>
              </Protegida>
            }
          />
          <Route
            path="/operacao/verificacoes"
            element={
              <Protegida permissao="driver:read">
                <Suspense fallback={<Carregando rotulo="Abrindo a fila" />}>
                  <FilaVerificacao />
                </Suspense>
              </Protegida>
            }
          />

          <Route
            path="/admin/usuarios"
            element={
              <Protegida permissao="user:read">
                <AdminUsuarios />
              </Protegida>
            }
          />
          <Route
            path="/admin/papeis"
            element={
              <Protegida permissao="role:read">
                <AdminPapeis />
              </Protegida>
            }
          />
          <Route
            path="/admin/auditoria"
            element={
              <Protegida permissao="admin:audit:read">
                <AdminAuditoria />
              </Protegida>
            }
          />

          <Route path="*" element={<NaoEncontrada />} />
        </Route>

        {/* A raiz e a vitrine publica, nao um redirecionamento para o painel.
            Antes disso o primeiro contato com o iFretes era a tela de senha:
            "/" mandava para /painel, que rebatia em /entrar para quem nao
            estava logado. Quem JA esta logado continua indo direto ao painel —
            para esse visitante a pagina de marketing nao tem funcao. */}
        <Route path="/" element={<Raiz />} />

        {/* `/inicio` mostra a pagina publica SEMPRE, com ou sem sessao.
            A raiz manda quem esta logado para o painel — o que e certo para
            usuario recorrente, mas tornava a pagina publica inalcancavel: nao
            havia link nenhum de volta a ela dentro do sistema. Quem quer rever
            a pagina (ou mostra-la a alguem) precisava sair da conta. */}
        <Route
          path="/inicio"
          element={
            <Suspense fallback={<Carregando rotulo="Carregando" />}>
              <Inicio />
            </Suspense>
          }
        />
      </Routes>
    </BrowserRouter>
  )
}
